// components/create/CoverImageSection.js
import React, { useState, useEffect } from 'react';
import '../../styles/CoverImageSection.scss';

export default function CoverImageSection({ coverImage, onPress }) {
  const [imageRatio, setImageRatio] = useState(16 / 9);
  const [isVertical, setIsVertical] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (coverImage?.uri) {
      const displayUri = getDisplayUri(coverImage);
      
      if (displayUri) {
        setImageError(false);
        setImageLoaded(false);
        
        const img = new Image();
        
        img.onload = () => {
          const ratio = img.width / img.height;
          const vertical = img.height > img.width;
          setImageRatio(ratio);
          setIsVertical(vertical);
          setImageLoaded(true);
          setImageError(false);
        };
        
        img.onerror = (error) => {
          console.error('Error loading image:', error);
          // Set defaults and mark as error
          setImageRatio(16 / 9);
          setIsVertical(false);
          setImageLoaded(true);
          setImageError(true);
        };
        
        img.src = displayUri;
      } else {
        // No valid URI, reset to defaults
        setImageRatio(16 / 9);
        setIsVertical(false);
        setImageLoaded(true);
        setImageError(false);
      }
    } else {
      // No image provided, show placeholder
      setImageRatio(16 / 9);
      setIsVertical(false);
      setImageLoaded(true);
      setImageError(false);
    }
  }, [coverImage]);

  const getDisplayUri = (image) => {
    if (!image) return null;
    
    if (image.isAnimated && image.type === 'gif') {
      return image.gifCdnUrl || 
             image.gifB2Url || 
             image.downsizedUrl || 
             image.uri;
    }
    
    return image.cdnUrl || 
           image.b2Url || 
           image.uri;
  };

  // Calculate dimensions
  const maxWidth = 760; // Container max-width minus padding
  const containerWidth = maxWidth;
  
  // Calculate natural height based on ratio
  const naturalHeight = containerWidth / imageRatio;
  
  // Set maximum height constraints
  const maxHeight = isVertical 
    ? Math.min(window.innerHeight * 0.6, maxWidth * 1.5)
    : maxWidth * 0.8;
  
  const minHeight = maxWidth * 0.35;
  
  // Apply constraints
  const containerHeight = Math.max(minHeight, Math.min(naturalHeight, maxHeight));
  
  // Determine if image will be cropped
  const willBeCropped = naturalHeight > containerHeight;

  const displayUri = getDisplayUri(coverImage);

  return (
    <div className="cover-image-section">
      <div
        className="cover-image-container"
        style={{
          width: `${containerWidth}px`,
          height: `${containerHeight}px`,
        }}
        onClick={onPress}
      >
        {displayUri ? (
          <>
            <img
              src={displayUri}
              alt="Cover"
              className="cover-image"
              onLoadStart={() => {
                setImageLoaded(false);
                setImageError(false);
              }}
              onLoad={() => {
                setImageLoaded(true);
                setImageError(false);
              }}
              onError={() => {
                setImageError(true);
                setImageLoaded(true);
              }}
            />
            
            {!imageLoaded && !imageError && (
              <div className="loading-overlay">
                <div className="spinner"></div>
              </div>
            )}
            
            {imageError && (
              <div className="error-overlay">
                <i className="icon-alert-circle"></i>
                <span>Tap to change image</span>
              </div>
            )}
            
            {coverImage?.isAnimated && !imageError && imageLoaded && (
              <div className="gif-badge">
                <i className="icon-play-circle"></i>
                <span>GIF</span>
              </div>
            )}
            
            {coverImage?.cdnUrl && process.env.NODE_ENV === 'development' && (
              <div className="debug-badge cdn-badge">
                <i className="icon-flash"></i>
                <span>CDN</span>
              </div>
            )}
            
            {willBeCropped && process.env.NODE_ENV === 'development' && (
              <div className="debug-badge crop-badge">
                <i className="icon-crop"></i>
                <span>Cropped</span>
              </div>
            )}
          </>
        ) : (
          <div className="placeholder">
            <i className="icon-image-outline"></i>
            <span>Tap to add cover image</span>
          </div>
        )}

        <div className="overlay">
          <div className="edit-button">
            <i className="icon-camera"></i>
          </div>
        </div>
      </div>
    </div>
  );
}